import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getProgress } from '@/lib/selections-api';
import type { SelectionRound, SelectionVoter } from '@littlebooker/dal';

interface BallotStatusProps {
  selectionId: string;
  round: SelectionRound;
  voters: SelectionVoter[];
  userName: (uid: string) => string;
}

/** Who has / hasn't submitted for a round — chasing, not peeking (no contents). */
export function BallotStatus({ selectionId, round, voters, userName }: BallotStatusProps) {
  const { data } = useQuery({
    queryKey: ['selection-progress', round.id],
    queryFn: () => getProgress(selectionId, round.id),
    refetchInterval: 15000,
  });
  const submitted = new Set(data?.submittedVoterIds ?? []);
  const draft = new Set(data?.draftVoterIds ?? []);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>Ballot status · round {round.ordinal}</CardDescription>
        <CardTitle className="text-lg">
          {data ? `${data.submitted}/${data.expected} submitted` : '…'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1 text-sm">
          {voters.map((v) => {
            const done = submitted.has(v.user_id);
            const started = !done && draft.has(v.user_id);
            return (
              <li key={v.user_id} className="flex items-center gap-2">
                <span
                  className={
                    done
                      ? 'text-primary'
                      : started
                        ? 'text-amber-600 dark:text-amber-500'
                        : 'text-muted-foreground'
                  }
                >
                  {done ? '✓' : started ? '◐' : '○'}
                </span>
                <span className={done ? '' : 'text-muted-foreground'}>{userName(v.user_id)}</span>
                <span
                  className={
                    started
                      ? 'ml-auto text-xs text-amber-600 dark:text-amber-500'
                      : 'ml-auto text-xs text-muted-foreground'
                  }
                >
                  {done ? 'submitted' : started ? 'draft — not submitted' : 'not started'}
                </span>
              </li>
            );
          })}
          {voters.length === 0 && <li className="text-muted-foreground">No voters on the roster.</li>}
        </ul>
      </CardContent>
    </Card>
  );
}
